/**
 * Integrytas Group - Common Security Module (CSM)
 * Page: APR.CONFIG
 *
 * Admin registry for the generic Approval Workflow engine: maintains
 * apr_config (entity, mode, status column, JSON workflow rules).
 * Approver decisions happen in the "My Approvals" inbox; the Common Routine
 * interceptor activates automatically for any entity with an active config.
 */

import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { YesNoBadge } from '../../components/common/YesNoBadge';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, PersonalizedField, GRID_TABLE_CLASS } from '../../components/personalization';
import type { GridColumnDef } from '../../components/personalization';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { apiDb } from '../../lib/api_client';
import { routedInsert, routedUpdate, routedDelete } from '../../lib/routed_writes';
import type { AuthSession } from '../../types/auth';
import { Modal } from '../../components/common/Modal';
import { ConfirmModal } from '../../components/common/ConfirmModal';
import { EmptyState } from '../../components/common/EmptyState';
import { ErrorBanner } from '../../components/common/ErrorBanner';
import { FieldRenderer, toUldCoreLayout } from '../../components/common/FieldRenderer';
import type { FieldDef } from '../../components/common/FieldRenderer';
import { useUldLayout } from '../../hooks/use_uld_layout';
import { useDirtyForm } from '../../hooks/use_dirty_form';
import { UdcFacets } from '../../components/common/UdcFacets';
import { FacetFilter } from '../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import { PageHeader } from '../../components/common/PageHeader';
import { ContentCard } from '../../components/common/ContentCard';
import { Search, Plus, RefreshCw } from 'lucide-react';
import { useUdcGrid } from '../../hooks/use_udc_grid';
import { useUdcFormFields } from '../../hooks/use_udc_form_fields';
import { registerUdcHost } from '../../lib/udc_hosts';
import { hasAccess } from '../../types/auth';

const PAGE_CODE = 'APR.CONFIG';

registerUdcHost('APR.CONFIG', 'apr_config', 'Approval Configurations');

const CONFIG_FIELD_DEFS: FieldDef[] = [
  { key: 'code', type: 'text', label: 'Internal Code', required: true, col_span: 1 },
  { key: 'entity', type: 'text', label: 'Entity (table)', col_span: 1 },
  { key: 'mode', type: 'select', label: 'Mode', col_span: 1 },
  { key: 'statusCol', type: 'text', label: 'Status Column', col_span: 1 },
];
const CONFIG_CORE_LAYOUT = toUldCoreLayout(CONFIG_FIELD_DEFS);

const CONFIG_GRID_COLS: GridColumnDef[] = [
  { key: 'internal_code', header: 'Code',   defaultWidth: 220 },
  { key: 'entity_name',   header: 'Entity', defaultWidth: 220 },
  { key: 'mode',          header: 'Mode',   defaultWidth: 160 },
  { key: 'is_active',     header: 'Active', defaultWidth: 90  },
];

interface ConfigRow {
  approval_config_id: number;
  internal_code: string;
  entity_name: string;
  mode: 'WRITE_THROUGH' | 'BLOCK_STAGE';
  status_column: string | null;
  workflow_rules: unknown;
  is_active: 'Y' | 'N';
  description: string | null;
}

export interface ApprovalConfigPageProps { session: AuthSession; }

export function ApprovalConfigPage({ session }: ApprovalConfigPageProps) {
  return (
    <PersonalizationProvider pageCode="APR.CONFIG" session={session}>
      <ApprovalConfigPageInner session={session} />
    </PersonalizationProvider>
  );
}

function ApprovalConfigPageInner({ session }: ApprovalConfigPageProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canAdd = hasAccess(session, PAGE_CODE, 'add');
  const canChange = hasAccess(session, PAGE_CODE, 'change');
  const canDelete = hasAccess(session, PAGE_CODE, 'delete');

  const { t } = useTranslation();
  const [rows, setRows] = useState<ConfigRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<ConfigRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ConfigRow | null>(null);
  const [filter, setFilter] = useState('');
  // §17.B5: per-column multi-value facets (logical columns of the list).
  const [facets, setFacets] = useState<{ mode: string[]; active: string[] }>({ mode: [], active: ['Y'] });
  const detail = useExpandedRows<number>();

  // §17.B13 related children (expand/contract). This registry lists configs only;
  // no per-row child collection is loaded here, so the control renders inert.
  // TODO: wire relations
  const buildRelatedGroups = (_id: number): RelatedChildGroup[] => [];
  const hasRelated = (id: number) => buildRelatedGroups(id).some(g => g.items.length > 0);

  const reload = async () => {
    setBusy(true); setError(null);
    try {
      const { data, error: e } = await apiDb.from('apr_config')
        .select('approval_config_id, internal_code, entity_name, mode, status_column, workflow_rules, is_active, description')
        .order('internal_code');
      if (e) throw e;
      setRows((data ?? []) as ConfigRow[]);
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };
  useEffect(() => { void reload(); }, []);

  // User-defined columns / facets an admin placed on this page (runtime, no code).
  const udcGrid = useUdcGrid('apr_config', 'APR.CONFIG', { pkColumn: 'approval_config_id', rows: rows as unknown as Record<string, unknown>[] });

  // Distinct facet options derived from the loaded rows (§17.B5).
  const facetOptions = useMemo(() => {
    const uniq = (vals: string[]) => [...new Set(vals.filter(Boolean))].sort();
    return {
      mode:   uniq(rows.map(r => r.mode)).map(v => ({ value: v, label: v })),
      active: [{ value: 'Y', label: t('common.yes', 'Yes') }, { value: 'N', label: t('common.no', 'No') }],
    };
  }, [rows, t]);

  const facetsActive = facets.mode.length + facets.active.length > 0;
  const anyFilterActive = facetsActive || filter.trim().length > 0;
  const clearAll = () => { setFilter(''); setFacets({ mode: [], active: [] }); };

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const matchesUdc = (r: ConfigRow) => udcGrid.matchRow(r as unknown as Record<string, unknown>);
    // OR within a facet, AND across facets.
    const matchesFacets = (r: ConfigRow) =>
      (facets.mode.length   === 0 || facets.mode.includes(r.mode)) &&
      (facets.active.length === 0 || facets.active.includes(r.is_active));
    const base = !q ? rows : rows.filter(r =>
      r.internal_code.toLowerCase().includes(q) || (r.entity_name ?? '').toLowerCase().includes(q));
    return base.filter(matchesUdc).filter(matchesFacets);
  }, [rows, filter, facets, udcGrid]);

  const handleDelete = async (r: ConfigRow): Promise<boolean> => {
    setBusy(true); setError(null);
    try {
      const { error: e } = await routedDelete(session, 'apr_config', 'approval_config_id', r.approval_config_id);
      if (e) throw e;
      await reload();
      return true;
    } catch (e) { setError((e as Error).message); return false; } finally { setBusy(false); }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-[18px] overflow-auto p-6">
        <PageHeader
          title={t('apr_config.title', 'Approval Configurations')}
          description={t('apr_config.list.description', 'Register which entities require approval and how each workflow is evaluated.')}
          actions={
            <>
              <button type="button" onClick={() => void reload()} className="btn-secondary flex items-center gap-1.5">
                <RefreshCw className="h-3.5 w-3.5" /> {t('apr_config.refresh', 'Refresh')}
              </button>
              {canAdd && (<button type="button" onClick={() => setCreating(true)} className="btn-primary flex items-center gap-1.5">
                <Plus className="h-3.5 w-3.5" /> {t('apr_config.new', 'New Config')}
              </button>)}
            </>
          }
        />

        {error && (
          <ErrorBanner
            message={t('apr_config.load_failed', "Couldn't load approval configurations.")}
            detail={error}
            retry_label={t('common.retry', 'Retry')}
            on_retry={() => void reload()}
            details_label={t('common.details', 'Details')}
          />
        )}

        <ContentCard className="flex min-h-0 flex-1 flex-col">
          <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-csm-line bg-white px-3 py-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-csm-ink4" aria-hidden />
              <input
                type="text"
                value={filter}
                onChange={e => setFilter(e.target.value)}
                placeholder={t('apr_config.filter_placeholder', 'Filter…')}
                className="h-8 w-72 rounded-csm-control border border-csm-border pl-8 pr-2 text-csm-md focus:border-primary focus:outline-none"
              />
            </div>
            <FacetFilter label={t('apr_config.field.mode', 'Mode')} options={facetOptions.mode} selected={facets.mode} onChange={v => setFacets(f => ({ ...f, mode: v }))} />
            <FacetFilter label={t('apr_config.col.active', 'Active')} options={facetOptions.active} selected={facets.active} onChange={v => setFacets(f => ({ ...f, active: v }))} />
            <UdcFacets grid={udcGrid} />
            <ClearFiltersButton active={anyFilterActive} onClear={clearAll} label={t('common.clear_filters', 'Clear filters')} />
            <div className="ml-auto flex items-center gap-2">
              {busy && <span className="text-csm-xs text-csm-muted">{t('apr_config.working', 'Working…')}</span>}
              <PersonalizationToggle />
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-auto">
        <PersonalizedGrid componentKey="grid" columns={[...CONFIG_GRID_COLS, ...udcGrid.columns]}>
          {(visibleCols, widths) => (
            <table className={GRID_TABLE_CLASS}>
              <thead className="bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                <tr>
                  <th className="w-7 border-b border-csm-border px-2 py-1 text-left" title={t('apr_config.related.header', 'Related records')} />
                  {visibleCols.map(col => (
                    <th key={col.key} className="border-b border-csm-border px-2 py-1 text-left"
                        style={widths[col.key] ? { width: widths[col.key] } : undefined}>{col.header}</th>
                  ))}
                  <th className="border-b border-csm-border px-2 py-1 text-left" style={{ width: '1%' }}>{t('apr_config.col.actions', 'Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <Fragment key={r.approval_config_id}>
                  <tr className="hover:bg-csm-surface-alt">
                    <td className="border-b border-csm-border-soft px-2 py-1 text-center align-middle" onClick={e => e.stopPropagation()}>
                      <RelatedExpandButton open={detail.isOpen(r.approval_config_id)} hasChildren={hasRelated(r.approval_config_id)} onToggle={() => detail.toggle(r.approval_config_id)} expandLabel={t('common.related.expand', 'Show related records')} collapseLabel={t('common.related.collapse', 'Hide related')} tooltip={t('common.related.tooltip', 'Show related records')} />
                    </td>
                    {visibleCols.map(col => (
                      <td key={col.key} className="border-b border-csm-border-soft px-2 py-1"
                          style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                        {col.key === 'internal_code' && <span className="font-mono text-csm-sm">{r.internal_code}</span>}
                        {col.key === 'entity_name'   && <span className="font-mono text-csm-sm">{r.entity_name}</span>}
                        {col.key === 'mode'          && <span className="text-csm-xs">{r.mode}</span>}
                        {col.key === 'is_active'     && <YesNoBadge value={r.is_active} yesLabel={t('common.boolean.active')} noLabel={t('common.boolean.inactive')} />}
                        {udcGrid.isUdcKey(col.key)   && udcGrid.renderCell(r as unknown as Record<string, unknown>, col.key)}
                      </td>
                    ))}
                    <td className="border-b border-csm-border-soft px-2 py-1 text-right whitespace-nowrap">
                      {canChange && (<button onClick={e => { e.stopPropagation(); setEditing(r); }} className="mr-1 rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-border-soft">{t('apr_config.edit', 'Edit')}</button>)}
                      {canDelete && (<button onClick={e => { e.stopPropagation(); setDeleteTarget(r); }} className="rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-0.5 text-csm-xs text-csm-danger hover:opacity-80">{t('apr_config.delete', 'Delete')}</button>)}
                    </td>
                  </tr>
                  {detail.isOpen(r.approval_config_id) && (
                    <tr><td colSpan={visibleCols.length + 2} className="border-b border-csm-border-soft bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}><RelatedChildrenPanel groups={buildRelatedGroups(r.approval_config_id)} session={session} /></td></tr>
                  )}
                  </Fragment>
                ))}
                {filtered.length === 0 && !busy && (
                  <tr><td colSpan={visibleCols.length + 2} className="py-12 text-center">
                    <EmptyState
                      variant={anyFilterActive ? 'no-match' : 'no-data'}
                      title={t('apr_config.empty', 'No approval configurations')}
                      action={
                        canAdd ? <button onClick={() => setCreating(true)} className="mt-2 rounded bg-primary px-3 py-1.5 text-csm-sm text-white hover:bg-primary-hover">
                          {t('apr_config.new_plus', '+ New Config')}
                        </button> : undefined
                      }
                    />
                  </td></tr>
                )}
              </tbody>
            </table>
          )}
        </PersonalizedGrid>
          </div>
        </ContentCard>
      </div>

      {(editing || creating) && (
        <ConfigModal session={session} row={editing}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); void reload(); }} />
      )}

      {deleteTarget && (
        <ConfirmModal
          title={t('apr_config.delete_title', 'Delete Config?')}
          message={t('apr_config.delete_message', 'Delete "{{code}}"? This cannot be undone.', { code: deleteTarget.internal_code })}
          onConfirm={async () => { const ok = await handleDelete(deleteTarget); if (ok) setDeleteTarget(null); }}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}

function ConfigModal({ session, row, onClose, onSaved }: {
  session: AuthSession; row: ConfigRow | null; onClose: () => void; onSaved: () => void;
}) {
  const { t } = useTranslation();
  const isCreate = row === null;

  const defaultRules = {
    trigger: { match: 'all', conditions: [{ column: 'total_amount', op: '>', value: '10000' }] },
    status: { pending: 'PENDING_APPROVAL', approved: 'APPROVED', rejected: 'REJECTED' },
    levels: [{ level: 1, quorum: { type: 'all' }, approvers: [{ type: 'manager', levels_up: 1 }] }],
  };

  const initialValues = {
    code: row?.internal_code ?? '',
    entity: row?.entity_name ?? '',
    mode: (row?.mode ?? 'WRITE_THROUGH') as 'WRITE_THROUGH' | 'BLOCK_STAGE',
    statusCol: row?.status_column ?? '',
    active: row ? row.is_active === 'Y' : false,
    description: row?.description ?? '',
    rulesText: (() => { try { return JSON.stringify(row?.workflow_rules ?? defaultRules, null, 2); } catch { return '{}'; } })(),
  };
  const { values: form, setValues: setForm, isDirty, reset } = useDirtyForm(initialValues);
  const udcForm = useUdcFormFields('apr_config', 'APR.CONFIG', { pkColumn: 'approval_config_id', pk: row?.approval_config_id });
  const { layout } = useUldLayout('APR.CONFIG.MODAL', useMemo(() => [...CONFIG_CORE_LAYOUT, ...udcForm.coreLayout], [udcForm.coreLayout]));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDiscard, setShowDiscard] = useState(false);
  const [internalCodeError, setInternalCodeError] = useState<string | null>(null);

  useEffect(() => {
    reset(initialValues);
    setInternalCodeError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row?.approval_config_id, isCreate]);

  const handleClose = () => {
    if (isDirty) { setShowDiscard(true); return; }
    onClose();
  };

  const save = async () => {
    setBusy(true); setError(null);
    try {
      let rules: unknown;
      try { rules = JSON.parse(form.rulesText); } catch { throw new Error(t('apr_config.err.invalid_json', 'Workflow rules must be valid JSON.')); }
      if (isCreate && !form.code.trim()) throw new Error(t('apr_config.err.code_required', 'Internal code is required.'));
      if (!form.entity.trim()) throw new Error(t('apr_config.err.entity_required', 'Entity (table) name is required.'));
      if (form.mode === 'WRITE_THROUGH' && !form.statusCol.trim()) throw new Error(t('apr_config.err.status_col_required', 'WRITE_THROUGH needs a status column.'));

      const payload = {
        entity_name: form.entity.trim(),
        mode: form.mode,
        status_column: form.statusCol.trim() || null,
        workflow_rules: rules,
        is_active: form.active ? 'Y' : 'N',
        description: form.description || null,
      };
      if (isCreate) {
        const { error: e } = await routedInsert(session, 'apr_config', { internal_code: form.code.trim().toUpperCase(), ...payload });
        if (e) throw e;
      } else {
        const { error: e } = await routedUpdate(session, 'apr_config', 'approval_config_id', row?.approval_config_id, payload);
        if (e) throw e;
      }
      onSaved();
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  const modalTitle = isCreate ? t('apr_config.modal.create', 'Create Approval Config') : t('apr_config.modal.edit', 'Edit Approval Config — {{code}}', { code: row?.internal_code ?? '' });

  return (
    <>
      <Modal
        title={modalTitle}
        onClose={handleClose}
        width="2xl"
        headerAction={session.effective_user.page_access.length > 0 && session.effective_user.page_access.every(p => p.can_view && p.can_change) ? null : undefined}
        footer={
          <>
            <button onClick={handleClose} className="rounded border border-csm-border bg-white px-3 py-1 text-csm-sm hover:bg-csm-border-soft">{t('apr_config.cancel', 'Cancel')}</button>
            <button onClick={() => void save()} disabled={busy} className="rounded border border-primary bg-primary px-3 py-1 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-60">{busy ? t('apr_config.saving', 'Saving…') : t('apr_config.save', 'Save')}</button>
          </>
        }
      >
        <div className="mb-3">
          <FieldRenderer
            fields={useMemo(() => [...CONFIG_FIELD_DEFS, ...udcForm.fieldDefs], [udcForm.fieldDefs])}
            layout={layout}
            columns={2}
            pageCode="APR.CONFIG.MODAL"
            session={session}
            render={(fld) => {
              if (fld.key === 'code') return (
                <PersonalizedField componentKey="modal" fieldKey="code" label={t('apr_config.field.code', 'Internal Code')}>
                  <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('apr_config.field.code', 'Internal Code')}</label>
                  <input type="text" value={form.code} disabled={!isCreate}
                         onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))}
                         onBlur={() => {
                           if (isCreate && form.code && !/^[A-Z][A-Z0-9_.]*$/.test(form.code)) {
                             setInternalCodeError(t('apr_config.err.code_format', 'Must start with a letter — uppercase letters, numbers, dots only.'));
                           } else {
                             setInternalCodeError(null);
                           }
                         }}
                         className="h-8 w-full rounded border border-csm-border px-2 font-mono text-csm-sm focus:border-csm-watermark focus:outline-none disabled:bg-csm-surface-alt disabled:text-csm-muted" />
                  {internalCodeError
                    ? <p className="text-csm-xs text-red-600 mt-1">{internalCodeError}</p>
                    : isCreate && <p className="text-csm-xs text-csm-ink-soft mt-1">{t('apr_config.code_help', 'E.g. CSM.MANAGER — uppercase, dots only')}</p>
                  }
                </PersonalizedField>
              );
              if (fld.key === 'entity') return (
                <PersonalizedField componentKey="modal" fieldKey="entity" label={t('apr_config.field.entity', 'Entity (table)')}>
                  <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('apr_config.field.entity', 'Entity (table)')}</label>
                  <input type="text" value={form.entity} onChange={e => setForm(f => ({ ...f, entity: e.target.value }))} placeholder="po_purchase_order"
                         className="h-8 w-full rounded border border-csm-border px-2 font-mono text-csm-sm focus:border-csm-watermark focus:outline-none" />
                </PersonalizedField>
              );
              if (fld.key === 'mode') return (
                <PersonalizedField componentKey="modal" fieldKey="mode" label={t('apr_config.field.mode', 'Mode')}>
                  <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('apr_config.field.mode', 'Mode')}</label>
                  <select value={form.mode} onChange={e => setForm(f => ({ ...f, mode: e.target.value as 'WRITE_THROUGH' | 'BLOCK_STAGE' }))}
                          className="h-8 w-full rounded border border-csm-border px-2 text-csm-sm focus:border-csm-watermark focus:outline-none">
                    <option value="WRITE_THROUGH">{t('apr_config.mode.write_through', 'WRITE_THROUGH (save then gate)')}</option>
                    <option value="BLOCK_STAGE">{t('apr_config.mode.block_stage', 'BLOCK_STAGE (stage until approved)')}</option>
                  </select>
                </PersonalizedField>
              );
              if (fld.key === 'statusCol') return (
                <PersonalizedField componentKey="modal" fieldKey="statusCol" label={t('apr_config.field.status_column', 'Status Column')}>
                  <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('apr_config.field.status_column', 'Status Column')} {form.mode === 'WRITE_THROUGH' ? t('apr_config.required', '(required)') : t('apr_config.na', '(n/a)')}</label>
                  <input type="text" value={form.statusCol} disabled={form.mode === 'BLOCK_STAGE'}
                         onChange={e => setForm(f => ({ ...f, statusCol: e.target.value }))} placeholder="approval_status_code"
                         className="h-8 w-full rounded border border-csm-border px-2 font-mono text-csm-sm focus:border-csm-watermark focus:outline-none disabled:bg-csm-surface-alt disabled:text-csm-muted" />
                </PersonalizedField>
              );
              if (udcForm.isUdcKey(fld.key)) return (
                <PersonalizedField componentKey="modal" fieldKey={fld.key} label={fld.label}
                  readonlyChildren={udcForm.renderControl(fld.key, { disabled: true })}>
                  {udcForm.renderControl(fld.key)}
                </PersonalizedField>
              );
              return null;
            }}
          />
        </div>

        <PersonalizedField componentKey="modal" fieldKey="rulesText" label={t('apr_config.field.workflow_rules', 'Workflow Rules (JSON)')}>
          <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('apr_config.field.workflow_rules', 'Workflow Rules (JSON)')}</label>
          <textarea rows={16} value={form.rulesText} onChange={e => setForm(f => ({ ...f, rulesText: e.target.value }))}
                    className="mb-1 w-full rounded border border-csm-border px-2 py-1 font-mono text-csm-xs focus:border-csm-watermark focus:outline-none" />
          <div className="mb-3 text-csm-2xs text-csm-ink-soft">
            <span className="font-mono">trigger.conditions</span> decide if approval is needed.
            <span className="font-mono"> levels[]</span> are sequential; each has a <span className="font-mono">quorum</span> (all|any|m_of_n)
            and <span className="font-mono">approvers[]</span> of type manager|role|users|hierarchy.
          </div>
        </PersonalizedField>

        <PersonalizedField componentKey="modal" fieldKey="description" label={t('apr_config.field.description', 'Description')}>
          <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('apr_config.field.description', 'Description')}</label>
          <textarea rows={2} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                    className="mb-3 w-full rounded border border-csm-border px-2 py-1 text-csm-sm focus:border-csm-watermark focus:outline-none" />
        </PersonalizedField>

        <PersonalizedField componentKey="modal" fieldKey="active" label={t('apr_config.field.active', 'Active')}>
          <label className="flex items-center gap-2 text-csm-sm text-csm-ink-soft">
            <input type="checkbox" checked={form.active} onChange={e => setForm(f => ({ ...f, active: e.target.checked }))} className="h-4 w-4 accent-gray-700" /> {t('apr_config.field.active', 'Active')}
          </label>
        </PersonalizedField>
        {error &&<div className="mt-3 rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-1 text-csm-sm text-csm-danger">{error}</div>}
      </Modal>

      {showDiscard && (
        <ConfirmModal
          title={t('apr_config.discard_title', 'Discard changes?')}
          message={t('apr_config.discard_message', 'You have unsaved changes that will be lost.')}
          confirmLabel={t('apr_config.discard_confirm', 'Discard')}
          cancelLabel={t('apr_config.discard_cancel', 'Keep editing')}
          danger={false}
          onConfirm={() => { reset(); onClose(); }}
          onCancel={() => setShowDiscard(false)}
        />
      )}
    </>
  );
}
